import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { EMPTY, Observable, Subject, catchError, from, switchMap, take, tap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private readonly firebaseAuth = inject(Auth);
    private isRefreshingToken = false;
    private tokenRefreshedSource = new Subject<void>();
    private tokenRefreshed$ = this.tokenRefreshedSource.asObservable();

    constructor(private readonly authService: AuthService) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401 || error.status === 403) {
                    // Check if token refresh is already in progress
                    if (this.isRefreshingToken) {
                        // Wait for the token to be refreshed, then retry the request
                        return this.tokenRefreshed$.pipe(
                            take(1),
                            switchMap(() => {
                                const token = this.authService.token;
                                if (token) {
                                    const authReq = request.clone({
                                        headers: request.headers.set('Authorization', `Bearer ${token}`),
                                    });
                                    return next.handle(authReq);
                                }
                                return EMPTY;
                            }),
                        );
                    }

                    // Start token refresh
                    this.isRefreshingToken = true;

                    // Check if currentUser exists before trying to get token
                    const currentUser = this.firebaseAuth.currentUser;
                    if (!currentUser) {
                        this.isRefreshingToken = false;
                        this.authService.logout(true);
                        return EMPTY;
                    }

                    // Convert the Promise from getIdToken to an Observable
                    return from(currentUser.getIdToken(true)).pipe(
                        tap((token: string) => {
                            // Update the token in AuthService
                            this.authService.token = token;
                            this.isRefreshingToken = false;
                            this.tokenRefreshedSource.next();
                        }),
                        switchMap((token: string) => {
                            if (token) {
                                // Clone the request and set the new token header
                                const authReq = request.clone({
                                    headers: request.headers.set('Authorization', `Bearer ${token}`),
                                });
                                // Retry the request with the new token
                                return next.handle(authReq);
                            } else {
                                // If no token, proceed to logout
                                this.authService.logout(true);
                                return EMPTY;
                            }
                        }),
                        catchError(() => {
                            // Handle if the token refreshment throws an error
                            this.isRefreshingToken = false;
                            this.authService.logout(true);
                            return EMPTY;
                        }),
                    );
                }
                // If the error is not 401 or 403, rethrow it
                return throwError(() => error);
            }),
        );
    }
}
